import client from "./client";
import type { PaginatedResponse } from "@/types/common";
import type {
  Document,
  DocumentAccessLog,
  DocumentTemplate,
  DocumentVersion,
  SignatureRequest,
} from "@/types/document";

export async function listDocuments(
  page = 1,
  perPage = 20,
  employeeId?: string,
  docType?: string
): Promise<PaginatedResponse<Document>> {
  const params: Record<string, string> = {
    page: String(page),
    per_page: String(perPage),
  };
  if (employeeId) params.employee_id = employeeId;
  if (docType) params.doc_type = docType;
  const res = await client.get<PaginatedResponse<Document>>("/documents", {
    params,
  });
  return res.data;
}

export async function uploadDocument(
  employeeId: string,
  name: string,
  docType: string,
  file: File,
  requiresSignature = false,
  notes?: string
): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append("employee_id", employeeId);
  formData.append("name", name);
  formData.append("doc_type", docType);
  formData.append("requires_signature", String(requiresSignature));
  if (notes) formData.append("notes", notes);
  formData.append("file", file);

  const res = await client.post<{ id: string }>("/documents/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function signDocument(documentId: string): Promise<void> {
  await client.post(`/documents/${documentId}/sign`);
}

// ── Versioning ──

export async function uploadNewVersion(
  documentId: string,
  file: File,
  name?: string,
): Promise<{ id: string; version: number }> {
  const fd = new FormData();
  fd.append("file", file);
  if (name) fd.append("name", name);
  const res = await client.post<{ id: string; version: number }>(
    `/documents/${documentId}/versions`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function listDocumentVersions(
  documentId: string,
): Promise<DocumentVersion[]> {
  const res = await client.get<DocumentVersion[]>(
    `/documents/${documentId}/versions`,
  );
  return res.data;
}

// ── Download with audit ──

export async function downloadDocument(
  documentId: string,
  filename?: string,
): Promise<void> {
  const res = await client.get(`/documents/${documentId}/download`, {
    responseType: "blob",
  });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function listAccessLogs(
  documentId: string,
): Promise<DocumentAccessLog[]> {
  const res = await client.get<DocumentAccessLog[]>(
    `/documents/${documentId}/access-logs`,
  );
  return res.data;
}

// ── Templates ──

export async function listTemplates(
  includeInactive = false,
): Promise<DocumentTemplate[]> {
  const res = await client.get<DocumentTemplate[]>("/documents/templates", {
    params: { include_inactive: includeInactive },
  });
  return res.data;
}

export async function createTemplate(
  body: Partial<DocumentTemplate>,
): Promise<DocumentTemplate> {
  const res = await client.post<DocumentTemplate>("/documents/templates", body);
  return res.data;
}

export async function updateTemplate(
  id: string,
  body: Partial<DocumentTemplate>,
): Promise<DocumentTemplate> {
  const res = await client.put<DocumentTemplate>(
    `/documents/templates/${id}`,
    body,
  );
  return res.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await client.delete(`/documents/templates/${id}`);
}

export async function instantiateTemplate(
  templateId: string,
  employeeId: string,
  overrideName?: string,
  overrideRequiresSignature?: boolean,
): Promise<{ id: string }> {
  const res = await client.post<{ id: string }>(
    `/documents/templates/${templateId}/instantiate`,
    {
      employee_id: employeeId,
      override_name: overrideName,
      override_requires_signature: overrideRequiresSignature,
    },
  );
  return res.data;
}

export async function bulkAssignTemplate(
  templateId: string,
  employeeIds: string[],
): Promise<{ created: number; failed: string[]; document_ids: string[] }> {
  const res = await client.post<{
    created: number;
    failed: string[];
    document_ids: string[];
  }>("/documents/bulk-assign", {
    template_id: templateId,
    employee_ids: employeeIds,
  });
  return res.data;
}

// ── Signature requests ──

export async function createSignatureRequest(
  documentId: string,
  signerUserId: string,
  expiresAt?: string,
): Promise<SignatureRequest> {
  const res = await client.post<SignatureRequest>("/documents/signatures/request", {
    document_id: documentId,
    signer_user_id: signerUserId,
    expires_at: expiresAt,
  });
  return res.data;
}

export async function listMyPendingSignatures(): Promise<SignatureRequest[]> {
  const res = await client.get<SignatureRequest[]>(
    "/documents/signatures/pending",
  );
  return res.data;
}

export async function listDocumentSignatures(
  documentId: string,
): Promise<SignatureRequest[]> {
  const res = await client.get<SignatureRequest[]>(
    `/documents/${documentId}/signatures`,
  );
  return res.data;
}

export async function markSignatureViewed(
  requestId: string,
): Promise<void> {
  await client.post(`/documents/signatures/${requestId}/view`);
}

export async function signSignatureRequest(
  requestId: string,
): Promise<void> {
  await client.post(`/documents/signatures/${requestId}/sign`);
}

export async function declineSignatureRequest(
  requestId: string,
  reason?: string,
): Promise<void> {
  await client.post(`/documents/signatures/${requestId}/decline`, { reason });
}

export async function sendSignatureReminder(
  requestId: string,
): Promise<void> {
  await client.post(`/documents/signatures/${requestId}/remind`);
}
